<template>
  <div class="page-container">
    <header class="header">
      <el-button text class="back-btn" @click="handleBack" aria-label="返回">
        <img src="@/assets/返回.svg" alt="返回" class="back-icon" />
      </el-button>
      <div class="header-title">库存查询</div>
      <div class="header-right"></div>
    </header>

    <main class="content">
      <div class="search-card">
        <div class="form-row">
          <div class="label">物料编码</div>
          <el-input
            v-model="materialNo"
            placeholder="请输入物料编码"
            clearable
            @keyup.enter="handleQuery"
          />
        </div>
        <div class="form-row">
          <div class="label">供应商</div>
          <el-select
            v-model="selectedSupplierId"
            class="supplier-select"
            placeholder="请输入供应商名称搜索"
            filterable
            remote
            :remote-method="handleSupplierSearch"
            :loading="supplierLoading"
            clearable
            @change="handleSupplierChange"
            @clear="clearSupplier"
          >
            <el-option
              v-for="s in supplierOptions"
              :key="s.id"
              :label="s.supplierName"
              :value="s.id"
            />
          </el-select>
        </div>
        <div class="form-actions">
          <el-button type="primary" round class="search-btn" :loading="loading" @click="handleQuery">
            查询
          </el-button>
          <el-button round class="reset-btn" @click="handleClear">清空</el-button>
        </div>
      </div>

      <div v-if="statusMsg" class="status-banner" :class="statusType">
        {{ statusMsg }}
      </div>

      <div v-if="searched && groupedRecords.length > 0" class="result-section">
        <div class="result-header">
          <span class="result-title">查询结果</span>
          <span class="result-count">共 {{ total }} 条</span>
        </div>

        <div v-for="(group, gIdx) in groupedRecords" :key="group.supplierName + '_' + gIdx" class="supplier-group">
          <div class="supplier-title">
            <span class="supplier-dot"></span>
            <span>{{ group.supplierName || "未知供应商" }}</span>
          </div>

          <div v-for="(item, mIdx) in group.materials" :key="item.materialId + '_' + mIdx" class="material-card">
            <div class="material-card-header">
              <div class="material-index">{{ groupStartIndex(gIdx) + mIdx + 1 }}</div>
              <div class="material-info">
                <div class="material-no">{{ item.materialNo || "-" }}</div>
                <div class="material-desc">{{ item.materialDesc || "" }}</div>
              </div>
            </div>
            <div class="material-stock-row">
              <div class="stock-tag good">
                <span class="stock-dot"></span>
                <span class="stock-label">良品</span>
                <span class="stock-num">{{ formatNum(item.goodStockNum) }}</span>
              </div>
              <div class="stock-tag bad">
                <span class="stock-dot"></span>
                <span class="stock-label">不良品</span>
                <span class="stock-num">{{ formatNum(item.badStockNum) }}</span>
              </div>
            </div>
            <div class="material-actions">
              <el-button size="small" type="primary" plain @click="openLocation(item)">
                查库位
              </el-button>
              <el-button size="small" type="success" plain @click="openFlow(item)">
                查流水
              </el-button>
              <el-button size="small" type="primary" plain @click="openQualityTransfer(item)">
                品质转换
              </el-button>
              <el-button size="small" type="warning" plain @click="openBatchAction(item, 'move')">
                移位
              </el-button>
            </div>
          </div>
        </div>

        <div v-if="Math.ceil(total / size) > 1" class="pagination">
          <el-pagination
            v-model:current-page="current"
            :page-size="size"
            :total="total"
            layout="prev, pager, next"
            @current-change="handleQuery"
          />
        </div>
      </div>

      <div v-else-if="searched" class="empty-state">
        <el-empty description="暂无数据" :image-size="80" />
      </div>

      <div v-else class="empty-state">
        <el-empty description="请输入物料编码查询" :image-size="80" />
      </div>
    </main>

    <!-- 库位弹窗 -->
    <el-dialog
      v-model="locationVisible"
      title="库位信息"
      width="90%"
      :close-on-click-modal="true"
      class="location-dialog"
      :append-to-body="true"
      :body-style="{ padding: '0', maxHeight: '65vh', overflow: 'hidden' }"
    >
      <div v-if="locationLoading" v-loading="true" element-loading-text="加载中..." style="min-height: 100px;"></div>
      <template v-else>
        <div class="location-material-info">
          <div class="location-material-no">{{ locationMaterial.materialNo }}</div>
          <div class="location-material-desc">{{ locationMaterial.materialDesc }}</div>
        </div>
        <div v-if="locationRecords.length === 0" class="location-empty">
          <el-empty description="暂无库位数据" :image-size="60" />
        </div>
        <div v-else class="location-list">
          <div v-for="(loc, idx) in locationRecords" :key="idx" class="location-item">
            <div class="location-left">
              <div class="location-name">{{ loc.wareName || loc.batchNo || "-" }}</div>
              <el-tag :type="loc.isBad === 1 ? 'danger' : 'success'" size="small" round>
                {{ loc.isBad === 1 ? "不良品" : "良品" }}
              </el-tag>
            </div>
            <div class="location-right">
              <span class="location-qty">{{ formatNum(loc.stockNum) }}</span>
            </div>
          </div>
        </div>
      </template>
    </el-dialog>

    <!-- 流水弹窗 -->
    <el-dialog
      v-model="flowVisible"
      title="流水明细"
      width="90%"
      :close-on-click-modal="true"
      class="flow-dialog"
      :append-to-body="true"
      :body-style="{ padding: '0', maxHeight: '65vh', overflow: 'hidden' }"
    >
      <div class="flow-dialog-content">
        <div class="flow-material-info">
          <div class="flow-material-no">{{ flowMaterial?.materialNo || "-" }}</div>
          <div class="flow-material-desc">{{ flowMaterial?.supplierName || "" }}</div>
        </div>

        <div v-if="flowLoading" v-loading="true" element-loading-text="加载中..." style="min-height: 100px;"></div>

        <template v-else>
          <div v-if="flowRecords.length" class="flow-date-list">
            <div
              v-for="(group, dateKey) in flowRecordsGroupedByDate"
              :key="dateKey"
              class="flow-date-item"
              @click="openFlowDetail(dateKey)"
            >
              <div class="flow-date-text">{{ dateKey }}</div>
              <div class="flow-date-stats">
                <span class="flow-stat-in">入库 {{ formatFlowNum(group.totalIn) }}</span>
                <span class="flow-stat-out">出库 {{ formatFlowNum(group.totalOut) }}</span>
              </div>
              <div class="flow-date-arrow">›</div>
            </div>
          </div>

          <div v-if="flowSearched && !flowLoading && flowRecords.length === 0" class="flow-empty">
            <el-empty description="暂无流水数据" :image-size="60" />
          </div>
        </template>
      </div>
    </el-dialog>

    <!-- 流水详情弹窗 -->
    <el-dialog
      v-model="flowDetailVisible"
      width="90%"
      class="flow-detail-dialog"
      :show-close="false"
      :close-on-click-modal="false"
      :append-to-body="true"
      :body-style="{ padding: '0', maxHeight: '65vh', overflow: 'hidden' }"
    >
      <template #default>
        <div class="flow-dd-fixed">
          <div class="flow-dd-top">
          <el-button size="small" @click="flowDetailVisible = false">
            ← 返回
          </el-button>
            <div class="flow-detail-stats">
              <span class="flow-stat-in">入库 {{ formatFlowNum(flowDetailSummary.totalIn) }}</span>
              <span class="flow-stat-out">出库 {{ formatFlowNum(flowDetailSummary.totalOut) }}</span>
            </div>
          </div>
          <div class="flow-dd-info">
            <div class="flow-dd-no">{{ flowMaterial?.materialNo || "-" }}</div>
            <div class="flow-dd-sub">{{ flowMaterial?.supplierName || "" }}</div>
          </div>
          <div class="flow-dd-date">{{ flowDetailDate }}</div>
        </div>
        <div class="flow-dd-scroll">
          <div v-if="flowDetailRecords.length === 0" class="flow-empty">
            <el-empty description="暂无数据" :image-size="60" />
          </div>
          <div v-else class="flow-record-list">
            <div
              v-for="(r, idx) in flowDetailRecords"
              :key="idx"
              class="flow-record-item"
              :class="isFlowIn(r) ? 'flow-in' : 'flow-out'"
            >
              <div class="flow-record-top">
                <span class="flow-record-idx">{{ idx + 1 }}</span>
                <div class="flow-record-tags">
                  <el-tag :type="isFlowIn(r) ? 'success' : 'danger'" size="small" round>
                    {{ isFlowIn(r) ? "入库" : isFlowOut(r) ? "出库" : "-" }}
                  </el-tag>
                  <el-tag v-if="getFlowType(r)" :type="getFlowType(r) === '不良品' ? 'danger' : 'info'" size="small" round>
                    {{ getFlowType(r) }}
                  </el-tag>
                </div>
                <span class="flow-record-time">{{ formatDisplayTime(r.createTime) || "-" }}</span>
              </div>
              <div class="flow-record-mid">
                <div class="flow-qty-block">
                  <span class="flow-qty-label">数量</span>
                  <span class="flow-qty-num">{{ formatFlowNum(r.materialNum) }}</span>
                </div>
                <div class="flow-qty-block">
                  <span class="flow-qty-label">结存</span>
                  <span class="flow-qty-num">{{ formatFlowNum(r.stockNum) }}</span>
                </div>
              </div>
              <div v-if="r.factoryName || r.remark" class="flow-record-bottom">
                <span v-if="r.factoryName" class="flow-extra">主机厂：{{ r.factoryName }}</span>
                <span v-if="r.remark" class="flow-extra">备注：{{ r.remark }}</span>
              </div>
            </div>
          </div>
        </div>
      </template>
    </el-dialog>

    <!-- 品质转换 / 移位：选择批次 -->
    <el-dialog
      v-model="qtVisible"
      :title="qtActionMode === 'move' ? '移位' : '品质转换'"
      width="90%"
      :close-on-click-modal="true"
      class="qt-dialog"
      :append-to-body="true"
      :body-style="{ padding: '0', maxHeight: '70vh', overflow: 'hidden' }"
    >
      <div v-if="qtLoading" v-loading="true" element-loading-text="加载中..." style="min-height: 100px;"></div>
      <div v-else class="qt-dialog-body">
        <div class="qt-material-info">
          <div class="qt-material-no">{{ qtMaterial.materialNo || "-" }}</div>
          <div class="qt-material-desc">{{ qtMaterial.materialDesc || "" }}</div>
          <div v-if="qtMaterial.supplierName" class="qt-material-supplier">
            {{ qtMaterial.supplierName }}
          </div>
        </div>
        <div v-if="qtGroups.length === 0" class="qt-empty">
          <el-empty description="暂无库存明细" :image-size="60" />
        </div>
        <div v-else class="qt-group-list">
          <div v-for="g in qtGroups" :key="g.key" class="qt-ware-group">
            <button type="button" class="qt-ware-head" @click="toggleQtGroup(g.key)">
              <span class="qt-ware-name">{{ g.wareName }}</span>
              <span class="qt-ware-count">库存 {{ formatNum(g.totalStockNum) }}</span>
              <span class="qt-ware-arrow" :class="{ collapsed: g.collapsed }">▾</span>
            </button>
            <div v-show="!g.collapsed" class="qt-batch-list">
              <button
                v-for="d in g.details"
                :key="d.id"
                type="button"
                class="qt-batch-item"
                :class="{ selected: isBatchSelected(d) }"
                @click="selectQtBatch(d)"
              >
                <div class="qt-batch-left">
                  <div class="qt-batch-main">
                    <span class="qt-batch-no">批次：{{ d.batchNo || "-" }}</span>
                    <el-tag :type="Number(d.isBad) === 1 ? 'danger' : 'success'" size="small" round>
                      {{ Number(d.isBad) === 1 ? "不良品" : "良品" }}
                    </el-tag>
                  </div>
                  <div class="qt-batch-sub">
                    托盘：{{ d.palletCode || "-" }} | 库存：{{ formatNum(d.stockNum) }}
                  </div>
                </div>
                <input
                  v-if="qtActionMode === 'move'"
                  class="qt-radio"
                  type="checkbox"
                  :checked="isBatchSelected(d)"
                  @click.stop="selectQtBatch(d)"
                />
                <input
                  v-else
                  class="qt-radio"
                  type="radio"
                  :checked="selectedQtId === d.id"
                  readonly
                />
              </button>
            </div>
          </div>
        </div>
        <div class="qt-footer">
          <div class="qt-tip">
            {{
              qtActionMode === "move"
                ? `可多选同物料批次（已选 ${selectedMoveBatches.length} 个）`
                : "点选一条批次即可进入转换（良品↔不良均可）"
            }}
          </div>
          <el-button
            type="primary"
            round
            class="qt-next-btn"
            :disabled="qtActionMode === 'move' ? selectedMoveBatches.length === 0 : !selectedQtRow"
            @click="openQtConfirm"
          >
            {{
              qtActionMode === "move"
                ? `开始移位（${selectedMoveBatches.length}）`
                : "下一步：扫描新托盘"
            }}
          </el-button>
        </div>
      </div>
    </el-dialog>

    <!-- 品质转换 / 移位：确认 -->
    <div v-if="showQtConfirmModal" class="qt-modal">
      <div class="qt-modal-overlay" @click="closeQtConfirmModal"></div>
      <div class="qt-modal-sheet">
        <div class="qt-modal-head">
          <div class="qt-modal-title">
            {{ qtActionMode === "move" ? "确认移位" : "确认转换" }}
          </div>
          <button type="button" class="qt-modal-close" @click="closeQtConfirmModal">×</button>
        </div>
        <div class="qt-modal-body">
          <div class="qt-modal-summary">
            <div class="qt-sum-main">{{ selectedQtRow?.materialNo || qtMaterial.materialNo || "-" }}</div>
            <div v-if="qtActionMode === 'move'" class="qt-sum-sub">
              已选 {{ selectedMoveBatches.length }} 个批次
              | 合计库存：{{ formatNum(selectedMoveTotalStock) }}
            </div>
            <div v-else class="qt-sum-sub">
              托盘：{{ selectedQtRow?.palletCode || "-" }}
              {{ selectedQtRow?.batchNo ? ` | 批次：${formatBatchNo(selectedQtRow.batchNo)}` : "" }}
              {{ Number(selectedQtRow?.isBad) === 1 ? " | 不良" : " | 良品" }}
              {{ selectedQtRow?.stockNum ? ` | 库存：${formatNum(selectedQtRow.stockNum)}` : "" }}
            </div>
          </div>

          <div class="qt-mode-switch">
            <button
              class="qt-mode-btn"
              :class="{ active: locationMode === 'scan' }"
              @click="locationMode = 'scan'"
            >
              扫码库位
            </button>
            <button
              class="qt-mode-btn"
              :class="{ active: locationMode === 'select' }"
              @click="locationMode = 'select'"
            >
              选择库位
            </button>
          </div>

          <div class="qt-row">
            <div class="qt-label">新托盘码</div>
            <div class="qt-dropdown-wrap">
              <div class="qt-input-with-scan">
                <input
                  v-model="palletKeyword"
                  class="qt-input"
                  placeholder="请输入新托盘码"
                  @focus="openPalletDropdown"
                  @input="handlePalletInput"
                />
                <button
                  v-if="palletKeyword"
                  class="qt-input-clear"
                  type="button"
                  @click="clearPalletKeyword"
                  aria-label="清除"
                >
                  ×
                </button>
                <button @click="startPalletScan" class="qt-scan-btn" aria-label="扫码" type="button">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="20" height="20">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                    <path d="M7 7h3v3H7zM14 7h3v3h-3zM7 14h3v3H7zM14 14h3v3h-3z" />
                  </svg>
                </button>
              </div>
              <div v-if="palletDropdownOpen" class="qt-dropdown">
                <div v-if="palletLoading" class="qt-status">加载中...</div>
                <div v-else-if="palletError" class="qt-status">{{ palletError }}</div>
                <div v-else-if="palletKeyword && palletOptions.length === 0" class="qt-status">暂无数据</div>
                <div v-else-if="palletOptions.length" class="qt-pallet-options">
                  <button
                    v-for="p in palletOptions"
                    :key="p.id"
                    type="button"
                    class="qt-pallet-option"
                    :class="{ active: selectedNewPalletId === String(p.id) }"
                    @click="confirmNewPallet(p)"
                  >
                    <div class="qt-pallet-option-main">{{ p.palletCode || "-" }}</div>
                    <div class="qt-pallet-option-sub">
                      {{ p.storeName ? `${p.storeName}` : "" }}
                      {{ p.wareAreaName ? ` | ${p.wareAreaName}` : "" }}
                      {{ p.wareName ? ` | ${p.wareName}` : "" }}
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <template v-if="locationMode === 'select'">
            <button class="qt-row-btn" type="button" @click="openWareSelector">
              <div class="qt-label">新库位</div>
              <div class="qt-value" :class="{ placeholder: !newWareText }">
                {{ newWareText || "请选择新库位" }}
              </div>
              <div class="qt-arrow">›</div>
            </button>
          </template>
          <template v-else>
            <div class="qt-row">
              <div class="qt-label">新库位</div>
              <div class="qt-dropdown-wrap">
                <input
                  v-model="scanWareNo"
                  class="qt-input"
                  :disabled="scanWareLoading || isHighRackPallet"
                  :placeholder="isHighRackPallet ? '高架仓库位已自动绑定' : '请扫描/输入库位码'"
                  @input="handleWareScan"
                />
                <div v-if="scanWareLoading" class="qt-dropdown-loading">加载中...</div>
              </div>
            </div>
            <div v-if="scanWareError" class="qt-form-error">{{ scanWareError }}</div>
          </template>

          <div v-if="qtActionMode !== 'move'" class="qt-row">
            <div class="qt-label">类型</div>
            <div class="qt-seg">
              <button
                type="button"
                class="qt-seg-btn"
                :class="{ active: direction === 0 }"
                :disabled="Number(selectedQtRow?.isBad) === 1"
                @click="direction = 0"
              >
                良 → 不
              </button>
              <button
                type="button"
                class="qt-seg-btn"
                :class="{ active: direction === 1 }"
                :disabled="Number(selectedQtRow?.isBad) === 0"
                @click="direction = 1"
              >
                不 → 良
              </button>
            </div>
          </div>

          <div class="qt-row">
            <div class="qt-label">数量</div>
            <div class="qt-qty-wrap">
              <input
                class="qt-input"
                inputmode="decimal"
                v-model="moveQty"
                :disabled="qtActionMode === 'move' && selectedMoveBatches.length > 1"
                :placeholder="
                  qtActionMode === 'move'
                    ? selectedMoveBatches.length > 1
                      ? '多批次不可改'
                      : '请输入移位数量'
                    : '请输入转换数量'
                "
              />
              <div
                v-if="qtActionMode === 'move' && selectedMoveBatches.length > 1"
                class="qt-qty-hint"
              >
                多批次自动移位全部数量
              </div>
            </div>
          </div>

          <div v-if="qtActionMode !== 'move'" class="qt-row">
            <div class="qt-label">备注</div>
            <input class="qt-input" v-model="remark" placeholder="请输入备注（选填）" />
          </div>
        </div>
        <div class="qt-modal-foot">
          <button class="qt-action-btn" type="button" :disabled="submitLoading" @click="submitQtTransfer">
            {{
              submitLoading
                ? "提交中..."
                : qtActionMode === "move"
                  ? "确认移位"
                  : "确认转换"
            }}
          </button>
          <div class="qt-form-tip">一次只能选择一个批次。</div>
        </div>
      </div>
    </div>

    <div v-if="showWareSelectModal" class="qt-modal">
      <div class="qt-modal-overlay" @click="closeWareSelectModal"></div>
      <div class="qt-modal-sheet">
        <div class="qt-modal-head">
          <div class="qt-modal-title">选择库位</div>
          <button type="button" class="qt-modal-close" @click="closeWareSelectModal">×</button>
        </div>
        <div class="qt-modal-body">
          <div class="qt-ware-list">
            <button
              v-for="ware in scanWareResults"
              :key="ware.id"
              class="qt-ware-item"
              type="button"
              @click="selectScannedWare(ware)"
            >
              <div class="qt-ware-item-name">{{ ware.wareName || ware.wareNo }}</div>
              <div class="qt-ware-item-area">{{ ware.wareAreaName }}</div>
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showWareSelector" class="qt-selector">
      <div class="qt-selector-overlay" @click="closeWareSelector"></div>
      <div class="qt-selector-panel">
        <header class="qt-selector-header">
          <button class="qt-selector-back" type="button" @click="closeWareSelector" aria-label="返回">×</button>
          <div class="qt-selector-title">库位搜索</div>
          <div class="qt-selector-right"></div>
        </header>
        <div class="qt-selector-search">
          <input
            type="text"
            v-model="locationSearchKeyword"
            placeholder="搜索库位"
            class="qt-search-input"
            @input="handleLocationSearch"
          />
        </div>
        <div class="qt-selector-warehouse-bar">
          <div v-if="warehouseLoading" class="qt-selector-warehouse-loading">加载仓库...</div>
          <div v-else class="qt-warehouse-tabs">
            <button
              v-for="w in warehouseList"
              :key="w.id"
              type="button"
              class="qt-warehouse-tab"
              :class="{ active: selectedWarehouseId === w.id }"
              @click="selectWarehouse(w)"
            >
              {{ w.wareName || "-" }}
            </button>
          </div>
        </div>
        <div v-if="wareSelectorLoading" class="qt-selector-loading">加载中...</div>
        <div v-else-if="wareSelectorError" class="qt-selector-error">{{ wareSelectorError }}</div>
        <div v-else class="qt-selector-body">
          <aside class="qt-areas">
            <button
              v-for="a in wareAreas"
              :key="a.id"
              type="button"
              class="qt-area-item"
              :class="{ active: activeAreaId === a.id }"
              @click="selectArea(a)"
            >
              {{ a.wareName || a.wareNo || "-" }}
            </button>
          </aside>
          <section class="qt-locations">
            <div class="qt-locations-title">{{ activeAreaName }}</div>
            <div class="qt-location-grid">
              <button
                v-for="l in activeLocations"
                :key="l.id"
                type="button"
                class="qt-location-card"
                :class="{ selected: selectedLocationId === String(l.id) }"
                @click="selectLocation(l)"
              >
                <div class="qt-location-name">{{ l.wareName || l.wareNo || "-" }}</div>
                <div class="qt-location-sub">剩余托盘位：{{ l.remainPallet ?? "-" }}</div>
              </button>
            </div>
          </section>
        </div>
        <div class="qt-selector-footer">
          <button
            class="qt-selector-confirm"
            type="button"
            :disabled="!selectedLocationId"
            @click="confirmWareSelector"
          >
            确定
          </button>
        </div>
      </div>
    </div>

    <CameraScan
      :visible="palletScanVisible"
      title="扫描新托盘码"
      @success="handlePalletScanSuccess"
      @close="palletScanVisible = false"
    />

    <div v-if="showSubmitConfirm" class="submit-confirm-modal">
      <div class="submit-confirm-overlay" @click="showSubmitConfirm = false"></div>
      <div class="submit-confirm-dialog">
        <div class="submit-confirm-icon">⚠</div>
        <div class="submit-confirm-title">确认提示</div>
        <div class="submit-confirm-message">
          {{
            qtActionMode === "move"
              ? "确定要执行确认移位吗？"
              : "确定要执行确认转换吗？"
          }}
        </div>
        <div class="submit-confirm-buttons">
          <button class="submit-confirm-btn cancel" type="button" @click="showSubmitConfirm = false">取消</button>
          <button class="submit-confirm-btn confirm" type="button" @click="handleConfirmSubmit">确定</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from "vue";
import { useRouter } from "vue-router";
import { ElMessage } from "element-plus";
import { getHfSupplierPage } from "@/api/hfSupplier";
import {
  getPalletCodeList,
  getStockWareList,
  getWareListByParentId,
  getWarePage,
} from "@/api/stockTransfer";
import { qualityTransferMove, changePalletLocation } from "@/api/stockCheck";
import CameraScan from "@/components/CameraScan";
import service from "@/utils/request";

export default {
  name: "MaterialStockQuery",
  components: { CameraScan },
  setup() {
    const router = useRouter();
    const handleBack = () => router.go(-1);

    // ===== 搜索 =====
    const materialNo = ref("");
    const selectedSupplierId = ref("");
    const selectedSupplier = ref(null);
    const supplierOptions = ref([]);
    const supplierLoading = ref(false);
    const supplierSearchSeq = ref(0);

    // ===== 结果 =====
    const records = ref([]);
    const total = ref(0);
    const current = ref(1);
    const size = ref(20);
    const loading = ref(false);
    const searched = ref(false);
    const statusMsg = ref("");
    const statusType = ref("info");

    const groupedRecords = computed(() => {
      const map = new Map();
      records.value.forEach((item) => {
        const key = item.supplierName || "未知供应商";
        if (!map.has(key)) {
          map.set(key, { supplierName: key, materials: [] });
        }
        map.get(key).materials.push(item);
      });
      return Array.from(map.values());
    });

    const groupStartIndex = (gIdx) => {
      let idx = 0;
      for (let i = 0; i < gIdx; i++) {
        idx += groupedRecords.value[i].materials.length;
      }
      return idx;
    };

    const formatNum = (val) => {
      const n = Number(val);
      if (!Number.isFinite(n)) return "0";
      if (Number.isInteger(n)) return String(n);
      return String(parseFloat(n.toFixed(2)));
    };

    // ===== 供应商搜索 =====
    const handleSupplierSearch = async (keyword) => {
      const seq = supplierSearchSeq.value + 1;
      supplierSearchSeq.value = seq;
      if (!keyword || !keyword.trim()) {
        supplierOptions.value = [];
        return;
      }
      supplierLoading.value = true;
      try {
        const res = await getHfSupplierPage({ keyword: keyword.trim() });
        if (seq !== supplierSearchSeq.value) return;
        const data = res?.data || {};
        supplierOptions.value = Array.isArray(data?.records) ? data.records : [];
      } catch (e) {
        if (seq !== supplierSearchSeq.value) return;
        supplierOptions.value = [];
      } finally {
        if (seq === supplierSearchSeq.value) {
          supplierLoading.value = false;
        }
      }
    };

    const handleSupplierChange = (val) => {
      if (val) {
        const s = supplierOptions.value.find((item) => item.id === val);
        selectedSupplier.value = s || null;
      } else {
        selectedSupplier.value = null;
      }
    };

    const clearSupplier = () => {
      selectedSupplier.value = null;
      selectedSupplierId.value = "";
    };

    // ===== 查询 =====
    const handleQuery = async (page) => {
      if (typeof page === "number") {
        current.value = page;
      }
      const matNo = (materialNo.value || "").trim();
      if (!matNo && !selectedSupplier.value) {
        ElMessage.warning("请输入物料编码或选择供应商");
        return;
      }
      if (loading.value) return;

      loading.value = true;
      searched.value = true;
      statusMsg.value = "";

      try {
        const params = {
          current: current.value,
          size: size.value,
        };
        if (matNo) params.materialNo = matNo;
        if (selectedSupplier.value) params.supplierId = selectedSupplier.value.id;

        const res = await service.get("/uic-system/materialstock/page", {
          params,
        });
        const data = res?.data || {};
        records.value = Array.isArray(data.records) ? data.records : [];
        total.value = Number(data.total) || 0;
        current.value = Number(data.current) || current.value;
      } catch (e) {
        records.value = [];
        total.value = 0;
        statusMsg.value = e?.message || "查询失败";
        statusType.value = "error";
      } finally {
        loading.value = false;
      }
    };

    const handleClear = () => {
      materialNo.value = "";
      selectedSupplier.value = null;
      selectedSupplierId.value = "";
      records.value = [];
      total.value = 0;
      current.value = 1;
      searched.value = false;
      statusMsg.value = "";
    };

    // ===== 库位弹窗 =====
    const locationVisible = ref(false);
    const locationLoading = ref(false);
    const locationMaterial = ref({});
    const locationRecords = ref([]);

    // ===== 流水弹窗 =====
    const flowVisible = ref(false);
    const flowMaterial = ref(null);
    const flowLoading = ref(false);
    const flowRecords = ref([]);
    const flowSearched = ref(false);

    // 流水详情弹窗
    const flowDetailVisible = ref(false);
    const flowDetailDate = ref("");
    const flowDetailRecords = ref([]);
    const flowDetailSummary = ref({ totalIn: 0, totalOut: 0 });

    const flowRecordsGroupedByDate = computed(() => {
      const groups = {};
      flowRecords.value.forEach((record) => {
        const dateStr = extractDate(record.createTime);
        if (!groups[dateStr]) {
          groups[dateStr] = { records: [], totalIn: 0, totalOut: 0 };
        }
        groups[dateStr].records.push(record);
        const num = Number(record.materialNum) || 0;
        if (isFlowIn(record)) {
          groups[dateStr].totalIn += num;
        } else if (isFlowOut(record)) {
          groups[dateStr].totalOut += num;
        }
      });
      return groups;
    });

    const openFlowDetail = (dateKey) => {
      const group = flowRecordsGroupedByDate.value[dateKey];
      if (!group) return;
      flowDetailDate.value = dateKey;
      flowDetailRecords.value = group.records;
      flowDetailSummary.value = { totalIn: group.totalIn, totalOut: group.totalOut };
      flowDetailVisible.value = true;
    };

    const extractDate = (v) => {
      if (!v) return "";
      return String(v).replaceAll("-", "/").slice(0, 10);
    };

    const formatDisplayTime = (v) => {
      if (!v) return "";
      const s = String(v);
      const idx = s.indexOf("T");
      if (idx >= 0) return s.slice(idx + 1, idx + 6);
      if (s.length > 10) return s.slice(11, 16);
      return "";
    };

    const formatFlowNum = (v) => {
      const n = Number(v);
      if (!Number.isFinite(n)) return "0";
      if (Number.isInteger(n)) return String(n);
      return String(parseFloat(n.toFixed(2)));
    };

    const pickFirst = (obj, keys) => {
      if (!obj) return "";
      for (const k of keys) {
        if (obj[k] !== null && obj[k] !== undefined && obj[k] !== "") return obj[k];
      }
      return "";
    };

    const isFlowIn = (r) => {
      if (!r) return false;
      const ft = String(r.flowType);
      const inTypes = new Set(["0", "1", "3", "4", "5", "9", "10", "20"]);
      const outTypes = new Set(["6", "7", "11", "13", "30"]);
      if (inTypes.has(ft)) return true;
      if (outTypes.has(ft)) return false;
      const name = String(pickFirst(r, ["flowTypeName", "bizTypeName", "typeName", "bizType", "type"]) || "");
      return name.includes("入");
    };

    const isFlowOut = (r) => {
      if (!r) return false;
      const ft = String(r.flowType);
      const outTypes = new Set(["6", "7", "11", "13", "30"]);
      const inTypes = new Set(["0", "1", "3", "4", "5", "9", "10", "20"]);
      if (outTypes.has(ft)) return true;
      if (inTypes.has(ft)) return false;
      const name = String(pickFirst(r, ["flowTypeName", "bizTypeName", "typeName", "bizType", "type"]) || "");
      return name.includes("出");
    };

    const getFlowType = (r) => {
      if (!r) return "";
      const ft = String(r.flowType || "");
      if (["0", "6", "11", "13"].includes(ft)) return "良品";
      if (["1", "7"].includes(ft)) return "不良品";
      return "";
    };

    const openFlow = async (item) => {
      if (!item?.materialId) {
        ElMessage.error("缺少物料信息");
        return;
      }
      flowMaterial.value = item;
      flowRecords.value = [];
      flowSearched.value = false;
      flowVisible.value = true;

      flowLoading.value = true;
      try {
        const res = await service.get("hf/uic-system/wareflowrecord/dayDetails", {
          params: { materialId: item.materialId },
        });
        const data = res?.data || {};
        flowRecords.value = Array.isArray(data) ? data : [];
        flowSearched.value = true;
      } catch (e) {
        flowRecords.value = [];
        flowSearched.value = true;
        ElMessage.error(e?.message || "查询流水失败");
      } finally {
        flowLoading.value = false;
      }
    };

    const openLocation = async (item) => {
      if (!item?.materialId) {
        ElMessage.error("缺少物料信息");
        return;
      }
      locationMaterial.value = item;
      locationVisible.value = true;
      locationLoading.value = true;
      locationRecords.value = [];

      try {
        const res = await service.get("/uic-system/hf_materialstock/page", {
          params: {
            materialId: item.materialId,
            materialNo: item.materialNo || "",
            current: 1,
            size: 200,
          },
        });
        const data = res?.data || {};
        let records = [];
        if (Array.isArray(data)) {
          records = data;
        } else if (Array.isArray(data.records)) {
          records = data.records;
        }
        const wareList = [];
        records.forEach((r) => {
          const details = Array.isArray(r.materialStockDetailList)
            ? r.materialStockDetailList
            : [];
          details.forEach((d) => {
            wareList.push({
              wareName: d.wareName || d.batchNo || "-",
              stockNum: d.stockNum || "0",
              isBad: d.isBad || 0,
            });
          });
        });
        locationRecords.value = wareList;
      } catch (e) {
        ElMessage.error(e?.message || "查询库位失败");
      } finally {
        locationLoading.value = false;
      }
    };

    // ===== 品质转换 / 移位 =====
    const qtVisible = ref(false);
    const qtLoading = ref(false);
    const qtActionMode = ref("quality"); // quality | move
    const qtMaterial = ref({});
    const qtGroups = ref([]);
    const selectedQtRow = ref(null);
    const selectedMoveBatches = ref([]);
    const selectedQtId = computed(() => String(selectedQtRow.value?.id || ""));
    const selectedMoveTotalStock = computed(() =>
      selectedMoveBatches.value.reduce(
        (sum, b) => sum + (Number(b.stockNum) || 0),
        0,
      ),
    );

    const showQtConfirmModal = ref(false);
    const showSubmitConfirm = ref(false);
    const moveQty = ref("");
    const remark = ref("");
    const submitLoading = ref(false);
    const direction = ref(0);

    const formatBatchNo = (batchNo, maxLength = 12) => {
      if (!batchNo) return "-";
      const str = String(batchNo).trim();
      if (str.length <= maxLength) return str;
      return str.substring(0, maxLength) + "...";
    };

    const buildQtGroups = (stockRecords) => {
      const map = new Map();
      (Array.isArray(stockRecords) ? stockRecords : []).forEach((r) => {
        const details = Array.isArray(r.materialStockDetailList)
          ? r.materialStockDetailList
          : [];
        details.forEach((d) => {
          const wareName = d.wareName || r.wareName || "未知库位";
          const key = `${d.wareId || ""}__${wareName}`;
          if (!map.has(key)) {
            map.set(key, {
              key,
              wareName,
              wareId: d.wareId || "",
              collapsed: true,
              totalStockNum: 0,
              details: [],
            });
          }
          const group = map.get(key);
          const stockNum = Number(d.stockNum) || 0;
          group.totalStockNum += stockNum;
          group.details.push({
            id: String(d.id || `${d.palletId}_${d.batchNo}_${d.isBad}`),
            materialId: d.materialId || r.materialId,
            materialNo: d.materialNo || r.materialNo,
            materialDesc: d.materialDesc || r.materialDesc,
            palletCode: d.palletCode || "",
            palletId: d.palletId || "",
            batchNo: d.batchNo || "",
            stockNum: d.stockNum || "0",
            isBad: d.isBad || 0,
            wareId: d.wareId || "",
            wareName,
            storeId: d.storeId || r.storeId || "",
            supplierId: r.supplierId || "",
            supplierName: r.supplierName || "",
          });
        });
      });
      return Array.from(map.values());
    };

    const openBatchAction = async (item, mode = "quality") => {
      if (!item?.materialId) {
        ElMessage.error("缺少物料信息");
        return;
      }
      qtActionMode.value = mode;
      qtMaterial.value = item;
      qtVisible.value = true;
      qtLoading.value = true;
      qtGroups.value = [];
      selectedQtRow.value = null;
      selectedMoveBatches.value = [];

      try {
        const params = {
          materialId: item.materialId,
          current: 1,
          size: 200,
        };
        if (item.supplierId) params.supplierId = item.supplierId;
        if (item.supplierName) params.supplierName = item.supplierName;

        const res = await service.get("/uic-system/materialstock/page", { params });
        const data = res?.data || {};
        const list = Array.isArray(data.records) ? data.records : [];
        qtGroups.value = buildQtGroups(list);
      } catch (e) {
        qtGroups.value = [];
        ElMessage.error(e?.message || "查询库存明细失败");
      } finally {
        qtLoading.value = false;
      }
    };

    const openQualityTransfer = async (item) => {
      await openBatchAction(item, "quality");
    };

    const openStockMove = async (item) => {
      await openBatchAction(item, "move");
    };

    const toggleQtGroup = (key) => {
      qtGroups.value = qtGroups.value.map((g) =>
        g.key === key ? { ...g, collapsed: !g.collapsed } : g
      );
    };

    const isBatchSelected = (d) => {
      if (!d) return false;
      if (qtActionMode.value === "move") {
        return selectedMoveBatches.value.some((b) => b.id === d.id);
      }
      return selectedQtId.value === String(d.id);
    };

    const selectQtBatch = (d) => {
      if (!d) return;

      // 移位：多选（同物料）
      if (qtActionMode.value === "move") {
        const idx = selectedMoveBatches.value.findIndex((b) => b.id === d.id);
        if (idx >= 0) {
          selectedMoveBatches.value.splice(idx, 1);
        } else {
          if (selectedMoveBatches.value.length > 0) {
            const firstNo = selectedMoveBatches.value[0]?.materialNo || "";
            const nextNo = d.materialNo || "";
            if (firstNo && nextNo && firstNo !== nextNo) {
              ElMessage.error("只能选择相同物料编码的批次");
              return;
            }
          }
          selectedMoveBatches.value.push(d);
        }
        selectedQtRow.value = selectedMoveBatches.value[0] || null;
        return;
      }

      // 品质转换：单选，选中后直接进入确认
      selectedQtRow.value = d;
      selectedMoveBatches.value = [d];
      resetQtConfirmForm();
      direction.value = Number(d.isBad) === 1 ? 1 : 0;
      showQtConfirmModal.value = true;
    };

    const resetQtConfirmForm = () => {
      newWareId.value = "";
      newWareText.value = "";
      selectedNewPallet.value = null;
      palletKeyword.value = "";
      palletOptions.value = [];
      palletDropdownOpen.value = false;
      palletError.value = "";
      moveQty.value = "";
      remark.value = "";
      scannedWareLocation.value = null;
      scanWareNo.value = "";
      scanWareError.value = "";
      locationMode.value = "scan";
    };

    const openQtConfirm = () => {
      if (qtActionMode.value === "move") {
        if (!selectedMoveBatches.value.length) {
          ElMessage.warning("请先勾选要移位的批次");
          return;
        }
        selectedQtRow.value = selectedMoveBatches.value[0];
        resetQtConfirmForm();
        // 与移位功能一致：默认填入合计库存；多批次时不可改
        moveQty.value = String(selectedMoveTotalStock.value || "");
        showQtConfirmModal.value = true;
        return;
      }

      if (!selectedQtRow.value) {
        ElMessage.warning("请先选择一条批次");
        return;
      }
      resetQtConfirmForm();
      direction.value = Number(selectedQtRow.value.isBad) === 1 ? 1 : 0;
      showQtConfirmModal.value = true;
    };

    const closeQtConfirmModal = () => {
      showQtConfirmModal.value = false;
    };

    const asId = (val) => {
      const s = String(val ?? "").trim();
      if (!s) return "";
      if (!/^\d+$/.test(s)) return "";
      return s;
    };

    // 新托盘
    const palletDropdownOpen = ref(false);
    const palletKeyword = ref("");
    const palletScanVisible = ref(false);
    const palletLoading = ref(false);
    const palletError = ref("");
    const palletOptions = ref([]);
    const selectedNewPallet = ref(null);
    const selectedNewPalletId = computed(() => String(selectedNewPallet.value?.id || ""));
    let palletSearchTimer = null;

    const isHighRackPallet = computed(
      () => Number(selectedNewPallet.value?.palletType) === 1
    );

    const clearPalletKeyword = () => {
      palletKeyword.value = "";
      selectedNewPallet.value = null;
      palletOptions.value = [];
      palletDropdownOpen.value = false;
    };

    const handlePalletSearch = async () => {
      const keyword = String(palletKeyword.value || "").trim();
      palletError.value = "";
      palletOptions.value = [];
      if (!keyword) return;

      palletLoading.value = true;
      try {
        const res = await getPalletCodeList({ palletCode: keyword });
        const data = res?.data;
        const list = Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data)
          ? data
          : [];

        if (list.length === 1) {
          confirmNewPallet(list[0]);
        } else {
          palletOptions.value = list;
        }
      } catch (e) {
        palletError.value = e?.message || "搜索失败";
      } finally {
        palletLoading.value = false;
      }
    };

    const openPalletDropdown = () => {
      palletDropdownOpen.value = true;
      palletError.value = "";
      palletOptions.value = [];
      if (String(palletKeyword.value || "").trim()) {
        handlePalletSearch();
      }
    };

    const startPalletScan = () => {
      palletScanVisible.value = true;
    };

    const handlePalletScanSuccess = (decodedText) => {
      const text = String(decodedText || "")
        .replace(/[\u200B-\u200D\uFEFF]/g, "")
        .replace(/[\r\n\t]/g, "")
        .trim();
      if (!text) return;
      palletKeyword.value = text;
      palletScanVisible.value = false;
      palletDropdownOpen.value = true;
      handlePalletInput();
    };

    const handlePalletInput = async () => {
      selectedNewPallet.value = null;
      palletDropdownOpen.value = true;
      if (palletSearchTimer) clearTimeout(palletSearchTimer);
      palletSearchTimer = setTimeout(() => {
        handlePalletSearch();
      }, 300);
    };

    const confirmNewPallet = (p) => {
      if (!p?.id) return;
      selectedNewPallet.value = p;
      palletKeyword.value = String(p?.palletCode || "");
      palletDropdownOpen.value = false;

      if (Number(p.palletType) === 1 && p.wareId) {
        locationMode.value = "scan";
        scannedWareLocation.value = {
          id: p.wareId,
          wareName: p.wareName,
          wareNo: p.wareNo,
          wareAreaName: p.wareAreaName,
        };
        scanWareNo.value = p.wareName || p.wareNo || "";
        newWareId.value = String(p.wareId);
      }
    };

    // 库位选择
    const showWareSelector = ref(false);
    const wareSelectorLoading = ref(false);
    const wareSelectorError = ref("");
    const warehouseLoading = ref(false);
    const warehouseList = ref([]);
    const selectedWarehouseId = ref("");
    const wareAreas = ref([]);
    const activeAreaId = ref("");
    const activeAreaName = ref("");
    const selectedArea = ref(null);
    const selectedLocation = ref(null);
    const selectedLocationId = computed(() => String(selectedLocation.value?.id || ""));
    const locationSearchKeyword = ref("");
    const newWareId = ref("");
    const newWareText = ref("");
    const locationMode = ref("scan");
    const scanWareNo = ref("");
    const scanWareLoading = ref(false);
    const scanWareError = ref("");
    const scanWareResults = ref([]);
    const scannedWareLocation = ref(null);
    const showWareSelectModal = ref(false);

    const activeLocations = computed(() => {
      const area = wareAreas.value.find((a) => String(a.id) === String(activeAreaId.value));
      const locations = Array.isArray(area?.wareList) ? area.wareList : [];
      if (!locationSearchKeyword.value.trim()) return locations;
      const keyword = locationSearchKeyword.value.toLowerCase();
      return locations.filter((loc) => {
        const name = (loc.wareName || loc.wareNo || "").toLowerCase();
        return name.includes(keyword);
      });
    });

    const handleLocationSearch = () => {};

    const loadWarehouses = async () => {
      warehouseLoading.value = true;
      warehouseList.value = [];
      selectedWarehouseId.value = "";
      try {
        const res = await getStockWareList();
        const data = res?.data;
        warehouseList.value = Array.isArray(data) ? data : [];
      } catch (e) {
        wareSelectorError.value = e?.message || "加载仓库失败";
      } finally {
        warehouseLoading.value = false;
      }
    };

    const selectWarehouse = async (warehouse) => {
      if (!warehouse?.id) return;
      selectedWarehouseId.value = warehouse.id;
      wareAreas.value = [];
      activeAreaId.value = "";
      activeAreaName.value = "";
      selectedArea.value = null;
      selectedLocation.value = null;
      wareSelectorError.value = "";
      wareSelectorLoading.value = true;
      try {
        const res = await getWareListByParentId({ id: warehouse.id });
        const data = res?.data;
        const nextAreas = Array.isArray(data?.wareList) ? data.wareList : [];
        wareAreas.value = nextAreas;
        const first = nextAreas[0];
        activeAreaId.value = first?.id || "";
        activeAreaName.value = first?.wareName || first?.wareNo || "";
      } catch (e) {
        wareSelectorError.value = e?.message || "加载失败";
      } finally {
        wareSelectorLoading.value = false;
      }
    };

    const openWareSelector = async () => {
      showWareSelector.value = true;
      wareSelectorError.value = "";
      if (!warehouseList.value.length) {
        await loadWarehouses();
      }
      if (!selectedWarehouseId.value && warehouseList.value.length) {
        await selectWarehouse(warehouseList.value[0]);
      }
    };

    const closeWareSelector = () => {
      showWareSelector.value = false;
    };

    const selectArea = (area) => {
      activeAreaId.value = area?.id || "";
      activeAreaName.value = area?.wareName || area?.wareNo || "";
    };

    const selectLocation = (loc) => {
      const area =
        wareAreas.value.find((a) => String(a.id) === String(activeAreaId.value)) || null;
      selectedArea.value = area;
      selectedLocation.value = loc;
    };

    const confirmWareSelector = () => {
      if (!selectedLocationId.value) return;
      newWareId.value = String(selectedLocation.value?.id || "");
      const areaName = selectedArea.value?.wareName;
      const locName = selectedLocation.value?.wareName;
      newWareText.value =
        areaName && locName
          ? `${areaName} / ${locName}`
          : String(locName || areaName || "");
      closeWareSelector();
    };

    const handleWareScan = async () => {
      if (isHighRackPallet.value) return;

      const wareNo = String(scanWareNo.value || "").trim();
      if (!wareNo) {
        scanWareError.value = "";
        scanWareResults.value = [];
        scannedWareLocation.value = null;
        newWareId.value = "";
        return;
      }

      scanWareLoading.value = true;
      scanWareError.value = "";
      try {
        const res = await getWarePage({ wareNo, wareType: 2 });
        const data = res?.data || {};
        const list = Array.isArray(data.records) ? data.records : [];
        if (list.length === 0) {
          scanWareError.value = "未找到匹配的库位";
          scannedWareLocation.value = null;
          newWareId.value = "";
        } else if (list.length === 1) {
          scannedWareLocation.value = list[0];
          newWareId.value = String(list[0]?.id || "");
          scanWareError.value = "";
        } else {
          scanWareResults.value = list;
          showWareSelectModal.value = true;
        }
      } catch (e) {
        scanWareError.value = e?.message || "查询失败";
        scannedWareLocation.value = null;
        newWareId.value = "";
      } finally {
        scanWareLoading.value = false;
      }
    };

    const selectScannedWare = (ware) => {
      scannedWareLocation.value = ware;
      newWareId.value = String(ware?.id || "");
      showWareSelectModal.value = false;
    };

    const closeWareSelectModal = () => {
      showWareSelectModal.value = false;
    };

    const normalizeQty = (val) => {
      const s = String(val ?? "").trim();
      if (!s) return null;
      const n = Number(s);
      if (!Number.isFinite(n) || n <= 0) return null;
      return n;
    };

    const buildMoveStockCheckDetailList = () => {
      const batches = selectedMoveBatches.value;
      if (!batches.length) {
        return { ok: false, list: [], error: "请选择要移位的批次", totalMoveNum: 0 };
      }

      for (const batchInfo of batches) {
        const storeId = asId(batchInfo.storeId);
        const materialId = asId(batchInfo.materialId);
        const wareId = asId(batchInfo.wareId);
        const palletId = asId(batchInfo.palletId);
        const batchNo = String(batchInfo.batchNo || "").trim() || "-";
        if (!storeId || !materialId || !wareId || !palletId || batchNo === "-") {
          return {
            ok: false,
            list: [],
            error: "批次数据不完整，无法移位",
            totalMoveNum: 0,
          };
        }
      }

      const totalStock = batches.reduce(
        (sum, b) => sum + (Number(b.stockNum) || 0),
        0,
      );

      let inputQty = normalizeQty(moveQty.value);
      if (batches.length === 1) {
        if (!inputQty) {
          return { ok: false, list: [], error: "请输入移位数量", totalMoveNum: 0 };
        }
        if (inputQty > totalStock) {
          return {
            ok: false,
            list: [],
            error: "移位数量不能超过总库存数量",
            totalMoveNum: 0,
          };
        }
      } else {
        // 多批次：自动移位全部数量
        inputQty = totalStock;
      }

      const list = batches.map((batchInfo) => {
        const moveQuantity =
          batches.length === 1
            ? inputQty
            : Number(batchInfo.stockNum) || 0;
        return {
          storeId: asId(batchInfo.storeId),
          materialId: asId(batchInfo.materialId),
          wareId: asId(batchInfo.wareId),
          palletId: asId(batchInfo.palletId),
          batchNo: String(batchInfo.batchNo || "").trim() || "-",
          isBad: Number(batchInfo.isBad) === 1 ? 1 : 0,
          moveNum: moveQuantity,
        };
      });

      const totalMoveNum = list.reduce(
        (sum, it) => sum + (Number(it.moveNum) || 0),
        0,
      );

      return { ok: true, list, error: "", totalMoveNum };
    };

    const submitQtTransfer = () => {
      if (qtActionMode.value === "move") {
        if (!selectedMoveBatches.value.length) {
          ElMessage.error("请选择要移位的批次");
          return;
        }
      } else if (!selectedQtRow.value) {
        ElMessage.error("请选择一个批次");
        return;
      }

      if (!asId(newWareId.value)) {
        ElMessage.error("请选择新库位");
        return;
      }
      if (!asId(selectedNewPallet.value?.id)) {
        ElMessage.error("请选择新托盘码（需从下拉结果中选中）");
        return;
      }

      if (qtActionMode.value === "move") {
        const built = buildMoveStockCheckDetailList();
        if (!built.ok) {
          ElMessage.error(built.error || "移位明细生成失败");
          return;
        }
      } else {
        const qty = normalizeQty(moveQty.value);
        if (!qty) {
          ElMessage.error("请输入数量");
          return;
        }
      }

      showSubmitConfirm.value = true;
    };

    const handleConfirmSubmit = async () => {
      showSubmitConfirm.value = false;
      const row = selectedQtRow.value || {};

      submitLoading.value = true;
      try {
        if (qtActionMode.value === "move") {
          const toWareId = asId(newWareId.value);
          const toPalletId = asId(selectedNewPallet.value?.id);
          if (!toWareId) {
            ElMessage.error("请选择新库位");
            return;
          }
          if (!toPalletId) {
            ElMessage.error("请选择新托盘码（需从下拉结果中选中）");
            return;
          }

          const built = buildMoveStockCheckDetailList();
          if (!built.ok) {
            ElMessage.error(built.error || "移位明细生成失败");
            return;
          }

          // 与 StockTransfer 移位功能参数一致
          await changePalletLocation({
            stockCheckDetailList: built.list,
            wareId: toWareId,
            moveNum: built.totalMoveNum,
            palletId: toPalletId,
          });
          ElMessage.success("移位成功");
          selectedMoveBatches.value = [];
        } else {
          const qty = normalizeQty(moveQty.value);
          const payload = {
            direction: Number(direction.value),
            fromPalletCode: String(row.palletCode || "").trim(),
            fromPalletId: String(row.palletId || ""),
            fromWareId: String(row.wareId || ""),
            storeId: String(row.storeId || ""),
            toWareId: String(newWareId.value || ""),
            toPalletId: String(selectedNewPallet.value?.id || ""),
            materialId: String(row.materialId || ""),
            batchNo: String(row.batchNo || ""),
            materialNum: qty,
            remark: String(remark.value || "").trim(),
          };
          await qualityTransferMove(payload);
          ElMessage.success("转换成功");
        }

        closeQtConfirmModal();
        resetQtConfirmForm();
        selectedQtRow.value = null;
        await openBatchAction(qtMaterial.value, qtActionMode.value);
        if (searched.value) {
          await handleQuery(current.value);
        }
      } catch (e) {
        ElMessage.error(e?.message || "提交失败");
      } finally {
        submitLoading.value = false;
      }
    };

    onMounted(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    });

    onUnmounted(() => {
      if (palletSearchTimer) clearTimeout(palletSearchTimer);
    });

    return {
      handleBack,
      materialNo,
      selectedSupplierId,
      selectedSupplier,
      supplierOptions,
      supplierLoading,
      handleSupplierSearch,
      handleSupplierChange,
      clearSupplier,
      records,
      total,
      current,
      size,
      loading,
      searched,
      statusMsg,
      statusType,
      groupedRecords,
      groupStartIndex,
      formatNum,
      handleQuery,
      handleClear,
      locationVisible,
      locationLoading,
      locationMaterial,
      locationRecords,
      openLocation,
      flowVisible,
      flowMaterial,
      flowLoading,
      flowRecords,
      flowSearched,
      flowRecordsGroupedByDate,
      formatDisplayTime,
      formatFlowNum,
      isFlowIn,
      isFlowOut,
      getFlowType,
      openFlow,
      flowDetailVisible,
      flowDetailDate,
      flowDetailRecords,
      flowDetailSummary,
      openFlowDetail,
      // 良品转不良 / 移位
      qtVisible,
      qtLoading,
      qtActionMode,
      qtMaterial,
      qtGroups,
      selectedQtRow,
      selectedQtId,
      selectedMoveBatches,
      selectedMoveTotalStock,
      isBatchSelected,
      openQualityTransfer,
      openStockMove,
      openBatchAction,
      toggleQtGroup,
      selectQtBatch,
      openQtConfirm,
      showQtConfirmModal,
      closeQtConfirmModal,
      formatBatchNo,
      locationMode,
      palletKeyword,
      palletDropdownOpen,
      palletLoading,
      palletError,
      palletOptions,
      selectedNewPalletId,
      openPalletDropdown,
      handlePalletInput,
      clearPalletKeyword,
      startPalletScan,
      confirmNewPallet,
      palletScanVisible,
      handlePalletScanSuccess,
      isHighRackPallet,
      scanWareNo,
      scanWareLoading,
      scanWareError,
      handleWareScan,
      newWareText,
      openWareSelector,
      showWareSelectModal,
      scanWareResults,
      selectScannedWare,
      closeWareSelectModal,
      showWareSelector,
      closeWareSelector,
      locationSearchKeyword,
      handleLocationSearch,
      warehouseLoading,
      warehouseList,
      selectedWarehouseId,
      selectWarehouse,
      wareSelectorLoading,
      wareSelectorError,
      wareAreas,
      activeAreaId,
      activeAreaName,
      selectArea,
      activeLocations,
      selectedLocationId,
      selectLocation,
      confirmWareSelector,
      moveQty,
      remark,
      direction,
      submitLoading,
      submitQtTransfer,
      showSubmitConfirm,
      handleConfirmSubmit,
    };
  },
};
</script>

<style scoped>
.page-container {
  min-height: 100vh;
  background: var(--background-color);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}

.page-container,
.page-container * {
  box-sizing: border-box;
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  padding-top: calc(10px + env(safe-area-inset-top));
  background-color: var(--header-background);
  color: var(--header-text);
  box-shadow: 0 2px 8px var(--shadow-light);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  height: calc(52px + env(safe-area-inset-top));
}

.back-btn {
  width: 36px;
  height: 36px;
}

.back-icon {
  width: 20px;
  height: 20px;
}

.header-title {
  font-size: 16px;
  font-weight: 600;
  flex: 1;
  text-align: center;
}

.header-right {
  width: 36px;
}

.content {
  padding: 8px 10px;
  margin-top: calc(52px + env(safe-area-inset-top));
}

/* Search */
.search-card {
  background: var(--card-background);
  border-radius: 10px;
  padding: 12px;
  border: 1px solid var(--border-color);
  margin-bottom: 10px;
}

.form-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}

.form-row:last-of-type {
  margin-bottom: 0;
}

.label {
  flex: 0 0 64px;
  font-size: 13px;
  color: var(--text-secondary);
}

.supplier-select {
  flex: 1;
}

.supplier-select :deep(.el-select__wrapper) {
  height: 38px;
  border-radius: 8px;
}

.form-actions {
  display: flex;
  gap: 8px;
  margin-top: 10px;
}

.search-btn {
  flex: 1;
  height: 40px;
  --el-button-bg-color: #5bc0de !important;
  --el-button-border-color: #5bc0de !important;
}

.reset-btn {
  flex: 0 0 auto;
  height: 40px;
}

/* Status */
.status-banner {
  margin-bottom: 10px;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 12px;
  text-align: center;
}

.status-banner.success {
  background: rgba(16, 185, 129, 0.1);
  color: #10b981;
}

.status-banner.error {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

/* Result */
.result-section {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.result-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.result-title {
  font-size: 15px;
  font-weight: 600;
  color: var(--text-primary);
}

.result-count {
  font-size: 12px;
  color: var(--text-secondary);
}

.supplier-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.supplier-title {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  font-size: 13px;
  font-weight: 600;
  color: var(--text-secondary);
}

.supplier-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--primary-color);
  flex-shrink: 0;
}

.material-card {
  background: var(--card-background);
  border-radius: 10px;
  border: 1px solid var(--border-color);
  overflow: hidden;
}

.material-card-header {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  gap: 10px;
}

.material-index {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: var(--primary-color);
  color: white;
  font-size: 11px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.material-info {
  flex: 1;
  min-width: 0;
}

.material-no {
  font-size: 14px;
  font-weight: 700;
  color: var(--text-primary);
  text-align: left;
}

.material-desc {
  font-size: 11px;
  color: var(--text-secondary);
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: left;
}

.material-stock-row {
  display: flex;
  padding: 0 12px 10px;
  gap: 10px;
}

.stock-tag {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
}

.stock-tag.good {
  background: rgba(16, 185, 129, 0.08);
}

.stock-tag.bad {
  background: rgba(239, 68, 68, 0.08);
}

.stock-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
}

.stock-tag.good .stock-dot {
  background: #10b981;
}

.stock-tag.bad .stock-dot {
  background: #ef4444;
}

.stock-label {
  color: var(--text-secondary);
  font-weight: 500;
}

.stock-num {
  font-weight: 700;
}

.stock-tag.good .stock-num {
  color: #10b981;
}

.stock-tag.bad .stock-num {
  color: #ef4444;
}

/* Pagination */
.pagination {
  display: flex;
  justify-content: center;
  padding: 12px 0;
}

/* Empty */
.empty-state {
  padding: 40px 0;
}

/* Location Dialog */
.location-material-info {
  padding: 10px 0;
  border-bottom: 1px solid var(--border-color);
  margin-bottom: 10px;
}

.location-material-no {
  font-size: 15px;
  font-weight: 700;
  color: var(--text-primary);
}

.location-material-desc {
  font-size: 12px;
  color: var(--text-secondary);
  margin-top: 2px;
}

.location-empty {
  padding: 20px 0;
}

.location-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.location-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  background: rgba(35, 177, 192, 0.03);
  border-radius: 8px;
  border: 1px solid var(--border-color);
}

.location-left {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.location-name {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.location-right {
  flex: 0 0 auto;
}

.location-qty {
  font-size: 16px;
  font-weight: 800;
  color: var(--primary-color);
}

/* Flow Dialog */
.flow-material-info {
  padding: 8px 0 12px;
  border-bottom: 1px solid var(--border-color);
  margin-bottom: 10px;
}

.flow-material-no {
  font-size: 15px;
  font-weight: 700;
  color: var(--text-primary);
}

.flow-material-desc {
  font-size: 12px;
  color: var(--text-secondary);
  margin-top: 2px;
}

.flow-date-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.flow-date-item {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  cursor: pointer;
  transition: border-color 0.2s;
}

.flow-date-item:active {
  border-color: var(--primary-color);
}

.flow-date-text {
  flex: 1;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
}

.flow-date-stats {
  display: flex;
  gap: 8px;
  margin-right: 6px;
}

.flow-stat-in {
  font-size: 12px;
  font-weight: 600;
  color: #10b981;
}

.flow-stat-out {
  font-size: 12px;
  font-weight: 600;
  color: #ef4444;
}

.flow-date-arrow {
  font-size: 16px;
  color: var(--text-secondary);
}

.flow-detail {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.flow-detail-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 0;
  border-bottom: 1px solid var(--border-color);
}

.flow-detail-date {
  flex: 1;
  font-size: 14px;
  font-weight: 700;
  color: var(--text-primary);
}

.flow-record-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.flow-record-item {
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid var(--border-color);
  background: var(--card-background);
}

.flow-record-item.flow-in {
  border-left: 3px solid #10b981;
  background: rgba(16, 185, 129, 0.03);
}

.flow-record-item.flow-out {
  border-left: 3px solid #ef4444;
  background: rgba(239, 68, 68, 0.03);
}

.flow-record-top {
  display: flex;
  align-items: center;
  gap: 6px;
}

.flow-record-idx {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: var(--border-color);
  font-size: 10px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-secondary);
  flex-shrink: 0;
}

.flow-record-tags {
  display: flex;
  gap: 4px;
}

.flow-record-time {
  margin-left: auto;
  font-size: 12px;
  color: var(--text-secondary);
}

.flow-record-mid {
  display: flex;
  gap: 16px;
  margin-top: 8px;
  padding: 8px 10px;
  background: rgba(35, 177, 192, 0.04);
  border-radius: 6px;
}

.flow-qty-block {
  display: flex;
  align-items: baseline;
  gap: 4px;
}

.flow-qty-label {
  font-size: 11px;
  color: var(--text-secondary);
}

.flow-qty-num {
  font-size: 16px;
  font-weight: 800;
  color: var(--text-primary);
}

.flow-record-bottom {
  margin-top: 6px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.flow-extra {
  font-size: 11px;
  color: var(--text-secondary);
  line-height: 1.4;
}

.flow-empty {
  padding: 20px 0;
  text-align: center;
}

/* Flow Dialog */
.flow-dialog :deep(.el-dialog) {
  height: auto !important;
  max-height: 80vh !important;
}

.flow-dialog :deep(.el-dialog__body) {
  padding: 0 !important;
}

.flow-dialog-content {
  max-height: 65vh;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 0 16px 16px;
}

/* Location Dialog */
.location-dialog :deep(.el-dialog) {
  height: auto !important;
  max-height: 80vh !important;
}

.location-dialog :deep(.el-dialog__body) {
  padding: 0 !important;
}

.location-dialog .location-material-info,
.location-dialog .location-list {
  padding-left: 16px;
  padding-right: 16px;
}

.location-dialog .location-list {
  max-height: 65vh;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

/* Flow Detail Dialog */
.flow-detail-dialog :deep(.el-dialog) {
  height: auto !important;
  max-height: 80vh !important;
}

.flow-detail-dialog :deep(.el-dialog__header) {
  display: none !important;
}

.flow-detail-dialog :deep(.el-dialog__body) {
  padding: 0 !important;
}

.flow-dd-fixed {
  flex-shrink: 0;
}

.flow-dd-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px 10px;
}

.flow-dd-info {
  padding: 0 16px 8px;
  border-bottom: 1px solid var(--border-color);
}

.flow-dd-no {
  font-size: 15px;
  font-weight: 700;
  color: var(--text-primary);
}

.flow-dd-sub {
  font-size: 12px;
  color: var(--text-secondary);
  margin-top: 2px;
}

.flow-dd-date {
  padding: 0 16px;
  height: 42px;
  line-height: 42px;
  font-size: 14px;
  font-weight: 700;
  color: var(--primary-color);
  border-bottom: 1px solid var(--border-color);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.flow-dd-scroll {
  max-height: 65vh;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 8px 16px 16px;
}

.flow-detail-stats {
  display: flex;
  gap: 10px;
}

.material-actions {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
  align-items: stretch;
  padding: 0 12px 12px;
}

.material-actions :deep(.el-button) {
  margin: 0 !important;
  width: 100%;
  height: 32px;
  min-height: 32px;
  padding: 0 2px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

/* 良品转不良 - 批次选择 */
.qt-material-info {
  padding: 12px 14px;
  border-bottom: 1px solid var(--border-color);
}

.qt-material-no {
  font-size: 15px;
  font-weight: 700;
  color: var(--text-primary);
}

.qt-material-desc,
.qt-material-supplier {
  font-size: 12px;
  color: var(--text-secondary);
  margin-top: 2px;
}

.qt-empty {
  padding: 20px 0;
}

.qt-dialog-body {
  display: flex;
  flex-direction: column;
  max-height: 70vh;
  min-height: 0;
}

.qt-group-list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 8px 10px 0;
}

.qt-ware-group {
  border: 1px solid var(--border-color);
  border-radius: 10px;
  margin-bottom: 8px;
  overflow: hidden;
  background: var(--card-background);
}

.qt-ware-head {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border: none;
  background: rgba(35, 177, 192, 0.08);
  cursor: pointer;
}

.qt-ware-name {
  flex: 1;
  text-align: left;
  font-size: 13px;
  font-weight: 700;
  color: var(--text-primary);
}

.qt-ware-count {
  font-size: 12px;
  font-weight: 600;
  color: var(--primary-color);
}

.qt-ware-arrow {
  font-size: 12px;
  color: var(--primary-color);
  transition: transform 0.2s;
}

.qt-ware-arrow.collapsed {
  transform: rotate(-90deg);
}

.qt-batch-list {
  padding: 6px;
}

.qt-batch-item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  background: var(--card-background);
  margin-bottom: 6px;
  text-align: left;
  cursor: pointer;
}

.qt-batch-item.selected {
  border-color: var(--primary-color);
  background: rgba(35, 177, 192, 0.12);
}

.qt-batch-item.disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.qt-batch-left {
  flex: 1;
  min-width: 0;
}

.qt-batch-main {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.qt-batch-no {
  flex: 1;
  min-width: 0;
  font-size: 13px;
  font-weight: 700;
  color: var(--text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.qt-batch-main :deep(.el-tag) {
  flex-shrink: 0;
}

.qt-batch-sub {
  margin-top: 4px;
  font-size: 12px;
  color: var(--text-secondary);
  word-break: break-all;
}

.qt-radio {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  accent-color: var(--primary-color);
}

.qt-footer {
  flex-shrink: 0;
  padding: 10px 12px 14px;
  border-top: 1px solid var(--border-color);
  background: var(--card-background);
}

.qt-tip {
  font-size: 12px;
  color: var(--text-secondary);
  margin-bottom: 8px;
  text-align: center;
}

.qt-next-btn {
  width: 100%;
  --el-button-bg-color: var(--primary-color);
  --el-button-border-color: var(--primary-color);
  --el-button-hover-bg-color: var(--primary-color);
  --el-button-hover-border-color: var(--primary-color);
}

/* 确认转换弹层 */
.qt-modal {
  position: fixed;
  inset: 0;
  z-index: 3000;
}

.qt-modal-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
}

.qt-modal-sheet {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--card-background);
  border-radius: 16px 16px 0 0;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
}

.qt-modal-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid var(--border-color);
}

.qt-modal-title {
  font-size: 15px;
  font-weight: 600;
}

.qt-modal-close {
  width: 36px;
  height: 36px;
  border: none;
  background: transparent;
  font-size: 22px;
  line-height: 36px;
  cursor: pointer;
}

.qt-modal-body {
  padding: 16px;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  flex: 1;
}

.qt-modal-summary {
  padding: 10px 12px;
  background: rgba(35, 177, 192, 0.12);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  margin-bottom: 12px;
}

.qt-sum-main {
  font-size: 13px;
  font-weight: 800;
}

.qt-sum-sub {
  margin-top: 6px;
  font-size: 12px;
  color: var(--text-secondary);
}

.qt-mode-switch {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}

.qt-mode-btn {
  flex: 1;
  height: 36px;
  border: 1px solid var(--border-color);
  border-radius: 10px;
  background: var(--card-background);
  font-size: 13px;
  color: var(--text-primary);
}

.qt-mode-btn.active {
  border-color: var(--primary-color);
  background: rgba(35, 177, 192, 0.15);
  font-weight: 700;
  color: var(--primary-color);
}

.qt-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}

.qt-label {
  font-size: 12px;
  color: var(--text-secondary);
  width: 64px;
  flex-shrink: 0;
  margin-bottom: 0;
}

.qt-input {
  flex: 1;
  min-width: 0;
  width: 100%;
  height: 40px;
  border: 1px solid var(--border-color);
  border-radius: 10px;
  padding: 0 12px;
  font-size: 14px;
  background: var(--card-background);
  color: var(--text-primary);
  box-sizing: border-box;
}

.qt-input:disabled {
  background: #f5f5f5;
  color: var(--text-secondary);
}

.qt-qty-wrap {
  flex: 1;
  min-width: 0;
}

.qt-qty-wrap .qt-input {
  width: 100%;
}

.qt-qty-hint {
  margin-top: 6px;
  font-size: 12px;
  color: var(--text-secondary);
}

.qt-dropdown-wrap {
  position: relative;
  flex: 1;
  min-width: 0;
}

.qt-input-with-scan {
  display: flex;
  align-items: center;
  gap: 6px;
}

.qt-input-with-scan .qt-input {
  flex: 1;
}

.qt-input-clear {
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  font-size: 18px;
  color: var(--text-secondary);
}

.qt-scan-btn {
  width: 40px;
  height: 40px;
  border: 1px solid var(--border-color);
  border-radius: 10px;
  background: var(--card-background);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--primary-color);
  flex-shrink: 0;
}

.qt-dropdown {
  position: absolute;
  left: 0;
  right: 0;
  top: 44px;
  z-index: 20;
  filter: drop-shadow(0 12px 24px rgba(0, 0, 0, 0.12));
}

.qt-status {
  padding: 12px;
  text-align: center;
  font-size: 13px;
  color: var(--text-secondary);
  background: var(--card-background);
  border: 1px solid var(--border-color);
  border-radius: 12px;
}

.qt-pallet-options {
  max-height: 220px;
  overflow: auto;
  border: 1px solid var(--border-color);
  border-radius: 12px;
  background: var(--card-background);
}

.qt-pallet-option {
  width: 100%;
  text-align: left;
  border: none;
  background: transparent;
  padding: 10px 12px;
  border-bottom: 1px solid var(--border-color);
}

.qt-pallet-option:last-child {
  border-bottom: none;
}

.qt-pallet-option.active {
  background: rgba(35, 177, 192, 0.12);
}

.qt-pallet-option-main {
  font-size: 13px;
  font-weight: 700;
}

.qt-pallet-option-sub {
  margin-top: 4px;
  font-size: 12px;
  color: var(--text-secondary);
}

.qt-dropdown-loading {
  margin-top: 6px;
  font-size: 12px;
  color: var(--text-secondary);
}

.qt-form-error {
  margin-top: -6px;
  margin-bottom: 10px;
  font-size: 12px;
  color: #ef4444;
}

.qt-row-btn {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  border: 1px solid var(--border-color);
  border-radius: 12px;
  background: var(--card-background);
  margin-bottom: 12px;
}

.qt-row-btn .qt-label {
  margin-bottom: 0;
  flex: 0 0 56px;
}

.qt-value {
  flex: 1;
  text-align: left;
  font-size: 14px;
  color: var(--text-primary);
}

.qt-value.placeholder {
  color: var(--text-secondary);
}

.qt-arrow {
  color: var(--text-secondary);
  font-size: 18px;
}

.qt-seg {
  display: flex;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.qt-seg-btn {
  flex: 1;
  height: 36px;
  border: 1px solid var(--border-color);
  border-radius: 10px;
  background: var(--card-background);
  color: var(--text-primary);
  font-weight: 600;
}

.qt-seg-btn.active {
  background: rgba(35, 177, 192, 0.15);
  border-color: var(--primary-color);
  color: var(--primary-color);
  font-weight: 700;
}

.qt-seg-btn:disabled {
  opacity: 0.45;
}

.qt-modal-foot {
  padding: 12px;
  border-top: 1px solid var(--border-color);
  padding-bottom: calc(12px + env(safe-area-inset-bottom));
}

.qt-action-btn {
  width: 100%;
  height: 44px;
  border: none;
  border-radius: 12px;
  background: var(--primary-color);
  color: #fff;
  font-size: 15px;
  font-weight: 700;
}

.qt-action-btn:disabled {
  opacity: 0.6;
}

.qt-form-tip {
  margin-top: 8px;
  text-align: center;
  font-size: 12px;
  color: var(--text-secondary);
}

.qt-ware-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.qt-ware-item {
  width: 100%;
  text-align: left;
  border: 1px solid var(--border-color);
  border-radius: 10px;
  padding: 12px;
  background: var(--card-background);
}

.qt-ware-item-name {
  font-size: 14px;
  font-weight: 700;
}

.qt-ware-item-area {
  margin-top: 4px;
  font-size: 12px;
  color: var(--text-secondary);
}

/* 库位选择器 */
.qt-selector {
  position: fixed;
  inset: 0;
  z-index: 4000;
}

.qt-selector-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
}

.qt-selector-panel {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 85vh;
  max-height: 600px;
  background: var(--card-background);
  border-radius: 16px 16px 0 0;
  display: flex;
  flex-direction: column;
}

.qt-selector-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  background: var(--primary-color);
}

.qt-selector-back {
  width: 36px;
  height: 36px;
  border: none;
  background: transparent;
  color: #fff;
  font-size: 22px;
}

.qt-selector-title {
  color: #fff;
  font-size: 15px;
  font-weight: 600;
}

.qt-selector-right {
  width: 36px;
}

.qt-selector-search {
  padding: 12px 16px;
  background: var(--primary-color);
}

.qt-search-input {
  width: 100%;
  height: 36px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 18px;
  padding: 0 16px;
  font-size: 14px;
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
  box-sizing: border-box;
}

.qt-search-input::placeholder {
  color: rgba(255, 255, 255, 0.7);
}

.qt-selector-warehouse-bar {
  padding: 10px 12px;
  border-bottom: 1px solid var(--border-color);
}

.qt-selector-warehouse-loading,
.qt-selector-loading,
.qt-selector-error {
  padding: 16px 12px;
  text-align: center;
  color: var(--text-secondary);
  font-size: 13px;
}

.qt-warehouse-tabs {
  display: flex;
  gap: 10px;
  overflow-x: auto;
}

.qt-warehouse-tab {
  flex: 0 0 auto;
  border: 1px solid var(--border-color);
  background: var(--card-background);
  color: var(--text-primary);
  padding: 8px 16px;
  border-radius: 999px;
  font-size: 13px;
}

.qt-warehouse-tab.active {
  border-color: var(--primary-color);
  background: var(--primary-color);
  color: #fff;
  font-weight: 700;
}

.qt-selector-body {
  flex: 1;
  min-height: 0;
  display: flex;
}

.qt-areas {
  width: 120px;
  padding: 8px;
  overflow: auto;
}

.qt-area-item {
  width: 100%;
  border: none;
  background: transparent;
  text-align: left;
  padding: 12px 10px;
  border-radius: 10px;
  margin-bottom: 8px;
  font-size: 14px;
  color: var(--text-primary);
}

.qt-area-item.active {
  font-weight: 700;
  box-shadow: 0 1px 0 var(--shadow-light);
  background: rgba(35, 177, 192, 0.1);
}

.qt-locations {
  flex: 1;
  padding: 12px;
  overflow: auto;
}

.qt-locations-title {
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 12px;
}

.qt-location-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.qt-location-card {
  border: 1px solid var(--border-color);
  background: var(--card-background);
  border-radius: 12px;
  padding: 16px 12px;
  text-align: center;
}

.qt-location-card.selected {
  border-color: var(--primary-color);
  background: rgba(35, 177, 192, 0.1);
}

.qt-location-name {
  font-size: 14px;
  font-weight: 700;
}

.qt-location-sub {
  margin-top: 6px;
  font-size: 12px;
  color: var(--text-secondary);
}

.qt-selector-footer {
  padding: 10px 12px;
  padding-bottom: calc(16px + env(safe-area-inset-bottom));
  border-top: 1px solid var(--border-color);
}

.qt-selector-confirm {
  width: 100%;
  height: 48px;
  border: none;
  border-radius: 12px;
  background: var(--primary-color);
  color: #fff;
  font-size: 15px;
  font-weight: 700;
}

.qt-selector-confirm:disabled {
  opacity: 0.5;
}

.submit-confirm-modal {
  position: fixed;
  inset: 0;
  z-index: 5000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.submit-confirm-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
}

.submit-confirm-dialog {
  position: relative;
  width: 80%;
  max-width: 320px;
  background: var(--card-background);
  border-radius: 16px;
  padding: 24px 20px 16px;
  text-align: center;
}

.submit-confirm-icon {
  font-size: 28px;
  margin-bottom: 8px;
}

.submit-confirm-title {
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 8px;
}

.submit-confirm-message {
  font-size: 14px;
  color: var(--text-secondary);
  margin-bottom: 16px;
}

.submit-confirm-buttons {
  display: flex;
  gap: 10px;
}

.submit-confirm-btn {
  flex: 1;
  height: 40px;
  border-radius: 10px;
  border: 1px solid var(--border-color);
  font-size: 14px;
}

.submit-confirm-btn.cancel {
  background: var(--card-background);
  color: var(--text-primary);
}

.submit-confirm-btn.confirm {
  background: var(--primary-color);
  border-color: var(--primary-color);
  color: #fff;
  font-weight: 700;
}
</style>

<!-- Non-scoped: el-dialog teleports to body, scoped CSS can't reach -->
<style>
.flow-dialog.el-dialog,
.location-dialog.el-dialog,
.flow-detail-dialog.el-dialog,
.qt-dialog.el-dialog {
  max-height: 80vh !important;
  overflow: hidden !important;
}

.flow-dialog .flow-dialog-content,
.location-dialog .location-list,
.flow-detail-dialog .flow-dd-scroll {
  max-height: 65vh;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.qt-dialog .el-dialog__body {
  padding: 0 !important;
  overflow: hidden !important;
}

.qt-dialog .el-dialog__header {
  border-bottom: 1px solid var(--border-color);
}

.qt-dialog .el-dialog__title {
  font-weight: 700;
  color: var(--text-primary);
}

.qt-dialog .qt-dialog-body {
  max-height: 70vh;
}

.qt-dialog .qt-group-list {
  max-height: none;
}

.qt-dialog .qt-next-btn.el-button--primary {
  background-color: var(--primary-color) !important;
  border-color: var(--primary-color) !important;
}

.qt-dialog .qt-next-btn.el-button--primary.is-disabled {
  opacity: 0.55;
}

.flow-detail-dialog .el-dialog__header {
  display: none !important;
}

.flow-dd-fixed {
  flex-shrink: 0;
}
</style>
